/**
 * Tutorial Card Block Schema
 *
 * sl-728o.7.1: GfxBlock for tutorial cards on the edgeless canvas.
 *
 * A tutorial card displays a tutorial title, description, and a start button.
 * Cards can be disabled (coming-soon) with a greyed-out button.
 */

import type { GfxCompatibleProps } from '@blocksuite/std/gfx';
import { GfxCompatible } from '@blocksuite/std/gfx';
import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/store';

// =============================================================================
// Props Type
// =============================================================================

export type TutorialCardBlockProps = {
  /** Tutorial session ID to start when clicking the button */
  tutorialSessionId: string;
  /** Card title */
  title: string;
  /** Card description (1-2 sentences) */
  description: string;
  /** Whether the card is disabled (coming soon) */
  disabled: boolean;
  /** Reason displayed when disabled (e.g. "Coming soon") */
  disabledReason: string;
  /** Required workspace role to start this tutorial (e.g. 'Admin', 'Owner') */
  requiredRole: string;
} & GfxCompatibleProps;

// =============================================================================
// Model Class
// =============================================================================

export class TutorialCardBlockModel extends GfxCompatible<TutorialCardBlockProps>(
  BlockModel
) {
  get isDisabled(): boolean {
    return this.props.disabled;
  }

  get displayTitle(): string {
    return this.props.title || 'Tutorial';
  }

  get displayDescription(): string {
    return this.props.description || '';
  }
}

// =============================================================================
// Defaults
// =============================================================================

const DEFAULT_CARD_WIDTH = 280;
const DEFAULT_CARD_HEIGHT = 160;

// =============================================================================
// Schema Definition
// =============================================================================

export const TutorialCardBlockSchema = defineBlockSchema({
  flavour: 'affine:tutorial-card',
  props: (): TutorialCardBlockProps => ({
    tutorialSessionId: '',
    title: 'Tutorial',
    description: '',
    disabled: false,
    disabledReason: '',
    requiredRole: '',

    // GfxCompatible props
    xywh: `[0,0,${DEFAULT_CARD_WIDTH},${DEFAULT_CARD_HEIGHT}]`,
    index: 'a0',
    lockedBySelf: false,
  }),
  metadata: {
    role: 'content',
    version: 1,
    parent: ['affine:note', '@root', 'affine:surface'], // Note, canvas root, and surface
    children: [],
  },
  toModel: () => new TutorialCardBlockModel(),
});

// =============================================================================
// Schema Extension
// =============================================================================

export const TutorialCardBlockSchemaExtension = BlockSchemaExtension(
  TutorialCardBlockSchema
);
